import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { distinctUntilChanged, filter, skip } from 'rxjs';
import { Module } from '../../models/module';
import { ChildModule } from '../../models/childModule';
import { TenantSessionState } from '../../store/state/tenant-session.state';
import { SetCurrentChildModule, SetCurrentModule } from '../../store/actions/tenant.actions';

@Injectable({ providedIn: 'root' })
export class RouterModuleResolverService {
    @SelectSnapshot(TenantSessionState.getTenantModules) private tenantModules!: Module[];

    constructor(private router: Router, private store: Store) {
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd)
        ).subscribe((e: NavigationEnd) => {
            if (!this.tenantModules?.length) return;
            this.dispatchFromUrl(e.urlAfterRedirects);
        });
        // The module list can change without a navigation: Winnow dashboard entries appear once the
        // tenant's features load. Re-resolve so the sidebar's current module carries them.
        this.store.select(TenantSessionState.getTenantModules).pipe(
            distinctUntilChanged(),
            skip(1),
            filter(modules => !!modules?.length && this.router.navigated)
        ).subscribe(() => this.dispatchFromUrl(this.router.url));
    }

    dispatchFromUrl(url: string): void {
        const { module, childModule } = this.resolveFromUrl(url);
        if (!module) return;
        this.store.dispatch(new SetCurrentModule(module));
        const resolvedChild = childModule ?? module.childModules?.[0];
        if (resolvedChild) {
            this.store.dispatch(new SetCurrentChildModule(resolvedChild));
        }
    }

    resolveFromUrl(url: string): { module: Module | null; childModule: ChildModule | null } {
        const urlParts = url.split(/[?\/]/).filter(s => s);

        const module = this.tenantModules?.find(m =>
            urlParts.includes(m.parentRoutePath?.replace(/^\//, '') ?? '')) ?? null;

        if (!module) return { module: null, childModule: null };

        const modulePath = module.parentRoutePath?.replace(/^\//, '');
        const moduleIndex = urlParts.indexOf(modulePath);
        const childLink = moduleIndex !== -1 ? urlParts[moduleIndex + 1] : undefined;
        const isDetail = moduleIndex !== -1 && urlParts.slice(moduleIndex + 2).some(s => s);

        // Match by link + linkParameter (e.g. dashboard with sisenseId)
        let childModule: ChildModule | null = module.childModules?.find(c =>
            urlParts.includes(c.link ?? '')
            && c.linkParameter
            && urlParts.includes(c.linkParameter)) ?? null;

        if (!childModule && childLink) {
            if (isDetail) {
                // Prefer the hidden (detail) variant of the child module
                childModule = module.childModules?.find(c => c.link === childLink && c.class === 'hide')
                    ?? module.childModules?.find(c => c.link === childLink)
                    ?? null;
            } else {
                // Prefer the visible (list) variant of the child module
                childModule = module.childModules?.find(c => c.link === childLink && c.class !== 'hide')
                    ?? module.childModules?.find(c => c.link === childLink)
                    ?? null;
            }
        }

        return { module, childModule };
    }
}
